import download from 'downloadjs';
import { isEmpty } from 'lodash';
import { SubmissionError } from 'redux-form';
import { ACTIONS } from '../Constants';
import { buildQueryString } from '../utils/StringUtils';
import { VerbFetch } from '../VerbFetch';

export const clearSearchedProfiles = () => (dispatch) => {
    dispatch({
        type: ACTIONS.CLEAR_SEARCHED_PROFILES
    });
};

/**
 *
 * @param {Object} params
 * @param {string} actionType
 * @return {function(*)}
 * @private
 */
const _getProfiles = (params, actionType) => {
    const { pathsContext, ...rest } = params;
    const baseUrl = `${pathsContext.serverUrl}/userprofiles`;
    const url = buildQueryString(rest, baseUrl);
    if (rest && rest.dispatchDisabled) {
        return VerbFetch(url);
    }
    return (dispatch) =>
        VerbFetch(url).then((body) => {
            dispatch({
                type: actionType,
                payload: {
                    profiles: body
                }
            });
            return body;
        });
};

/**
 *
 * @param {Object} query
 * @return {function(*)}
 */
// eslint-disable-next-line consistent-return
export const getManyProfiles = (query) => {
    if (isEmpty(query)) {
        throw new Error('Error in getManyProfiles. Must provide query parameters');
    }
    return _getProfiles(query, ACTIONS.GET_MANY_PROFILES);
};

/**
 *
 * @param {Object} query
 * @return {function(*)}
 */
export const getFilteredProfiles = (query) => _getProfiles(query, ACTIONS.GET_FILTERED_PROFILES);

/**
 *
 * @param {Object} params
 * @param {String} params.profileId
 * @param {boolean} [params.isLoggedInUser=false]
 * @return {function(*)}
 */
export const getProfile = (params) => {
    const { profileId, isLoggedInUser = false, pathsContext } = params;
    return (dispatch) => {
        dispatch({
            type: ACTIONS.GET_PROFILE_STARTED
        });
        return VerbFetch(`${pathsContext.serverUrl}/userprofiles/${profileId}`)
            .then((body) => {
                dispatch({
                    type: ACTIONS.GET_PROFILE,
                    payload: {
                        profile: body,
                        isLoggedInUser
                    }
                });
                return body;
            })
            .catch((err) => {
                console.error(err);
            });
    };
};

export const getCollectionProfiles = (params) => {
    const { collectionId, limit, page, ignoreDispatch, teamId, count = false, pathsContext } = params;
    const baseUrl = `${pathsContext.serverUrl}/userprofiles/collection/${collectionId}`;
    const url = buildQueryString({ limit, page, teamId, count }, baseUrl);
    if (ignoreDispatch) {
        return VerbFetch(url);
    }
    return (dispatch) =>
        VerbFetch(url)
            .then((body) => {
                const { userProfiles } = body;
                dispatch({
                    type: ACTIONS.GET_FILTERED_PROFILES,
                    payload: {
                        profiles: userProfiles
                    }
                });
                return body;
            })
            .catch((err) => {
                console.error(err);
            });
};

export const putProfileCurrentCollection = (params) => {
    const { currentUserCollection, userProfileId, pathsContext } = params;

    return (dispatch) => {
        const fetchOptions = {
            method: 'PUT',
            body: {
                currentUserCollection
            }
        };

        return VerbFetch(`${pathsContext.serverUrl}/userprofiles/${userProfileId}/usercollection`, fetchOptions)
            .then((response) => {
                dispatch({
                    type: ACTIONS.PUT_PROFILE_COLLECTION,
                    payload: {
                        profile: response
                    }
                });
            })
            .catch((err) => {
                console.error(err);
            });
    };
};

export const updateProfileOnLoginSuccess = (params) => {
    const { id, updateParams, teamPermalink, profileMetadata, pathsContext } = params;

    return (dispatch) => {
        const putBody = {
            updateParams,
            teamPermalink
        };
        if (profileMetadata) {
            putBody.profileMetadata = profileMetadata;
        }
        const fetchOptions = {
            method: 'PUT',
            body: putBody
        };

        return VerbFetch(`${pathsContext.serverUrl}/userprofiles/${id}/loginsuccess`, fetchOptions)
            .then((response) => {
                dispatch({
                    type: ACTIONS.PUT_LAST_SIGN_IN_DATETIME,
                    payload: response
                });
            })
            .catch((err) => {
                console.error(err);
            });
    };
};

/**
 *
 * @param {*} params
 */
export const deleteProfile = (params) => {
    const { id } = params;
    return (dispatch) => {
        dispatch({
            type: ACTIONS.DELETE_PROFILE,
            payload: {
                id
            }
        });
    };
};

/**
 *
 * @param params
 * @returns {function(*)}
 * @private
 */
export const realDeleteProfile = (params) => {
    const { invitedUsersToDelete, pathsContext } = params;
    return (dispatch) => {
        const fetchOptions = {
            method: 'PUT',
            body: { invitedUsersToDelete }
        };

        return VerbFetch(`${pathsContext.adminServerUrl}/userprofiles/deleteinvites`, fetchOptions)
            .then((response) => {
                dispatch({
                    type: ACTIONS.REAL_DELETE_PROFILES,
                    payload: response
                });
            })
            .catch((err) => {
                console.error(err);
            });
    };
};

export const updateResponseVariables = (params) => {
    const { id, responseVariables, pathsContext } = params;
    const fetchOptions = {
        method: 'PUT',
        body: responseVariables
    };
    return VerbFetch(`${pathsContext.serverUrl}/userprofiles/${id}/responsevariables`, fetchOptions);
};

export const updateEmailConfiguration = (params) => {
    const { id, emailConfig, pathsContext } = params;

    return (dispatch) => {
        const fetchOptions = {
            method: 'PUT',

            // format for this should be { "emailConfigs.FIELD_NAME": true || false }
            body: emailConfig
        };

        return VerbFetch(`${pathsContext.serverUrl}/userprofiles/${id}/emailconfiguration`, fetchOptions)
            .then((response) => {
                dispatch({
                    type: ACTIONS.UPDATE_PROFILE,
                    payload: { profile: response }
                });
            })
            .catch((err) => {
                console.error(err);
            });
    };
};

export const updateLoggedInUser = (params) => {
    const { badges, points } = params;

    return (dispatch) => {
        dispatch({
            type: ACTIONS.UPDATE_LOGGED_IN_USER,
            payload: {
                badges,
                points
            }
        });
    };
};

export const loginUserSuccess = (params) => {
    const { userProfile, authContext } = params;

    return (dispatch) => {
        dispatch({
            type: ACTIONS.LOGIN_SUCCESS,
            payload: {
                userProfile,
                idToken: authContext.token
            }
        });
    };
};

export const authenticateLoggedInUserOnly = () => {
    return (dispatch) => {
        dispatch({
            type: ACTIONS.AUTHENTICATE_ONLY,
        });
    };
};

export const createSchedule = (params) => {
    const { pathsContext, ...rest } = params;
    const fetchOptions = {
        method: 'POST',
        body: rest
    };
    return VerbFetch(`${pathsContext.serverUrl}/userprofiles/createschedule`, fetchOptions).then((response) => {
        download(response, 'verb-schedule.ics', 'text/calendar');
    });
};

export const createCalendarEvent = (params) => {
    const { pathsContext, ...rest } = params;
    const fetchOptions = {
        method: 'POST',
        body: rest
    };
    return VerbFetch(`${pathsContext.serverUrl}/userprofiles/createcalendarevent`, fetchOptions).then((response) => {
        download(response, 'milestone-event.ics', 'text/calendar');
    });
};

export const clearProfiles = () => (dispatch) =>
    dispatch({
        type: ACTIONS.CLEAR_PROFILES
    });

export const updateTeamsForUser = (params) => {
    const { profile } = params;

    return (dispatch) => 
            dispatch({
                type: ACTIONS.UPDATE_PROFILE,
                payload: {
                    profile
                }
            });
}
